// @safetyhub/report-composer — 미적용 바인딩 placeholder (admin 편집/미리보기 전용)
//
// 실데이터가 적용되지 않아 resolve 값이 빈값(undefined/null/'')인 카탈로그 바인딩 노드/셀에
// 카탈로그의 한글 label을 회색 `{label}` 형태로 표시한다. 어느 필드가 어디에 바인딩되는지
// 편집 중 한눈에 보이게 하는 편집 보조이며, **web 실렌더(옵션 off)에는 표시하지 않는다**.

import type {
  Binding,
  BindingFormat,
  CatalogField,
  CatalogIndex,
  CellAggregate,
  CellAggregateFn,
  CellCompute,
  CellComputeOp,
  DataSourceDef,
} from '@safetyhub/shared';

import type { CSSProperties } from 'react';

import { applyFormat } from './resolver';
import type { BindingPlaceholdersMode, BindingProvenanceContext } from './types';

/**
 * placeholder 산출에 필요한 binding 필드만(full Binding도 구조적 할당).
 * lifecycle은 'unresolvable-only' 모드 판정에만 쓰여 optional — 미보유 호출부(라벨 조회 등)와 하위호환.
 */
type PlaceholderBinding = Pick<Binding, 'bindingKey' | 'catalogKey' | 'select'> &
  Partial<Pick<Binding, 'lifecycle'>>;

/** placeholder 게이트 ctx — RenderContext의 placeholder 관련 축만(구조적 부분집합). */
interface PlaceholderCtx {
  bindingPlaceholders?: BindingPlaceholdersMode;
  catalog?: CatalogIndex;
}

/** placeholder 텍스트 색 — 기존 placeholder 톤(gray-400). */
export const BINDING_PLACEHOLDER_COLOR = '#9ca3af';

/**
 * 'unresolvable-only' 모드 placeholder 색 — amber-600. 기존 회색(전체 바인딩 보기)과 톤을 구분해
 * "저작 중엔 원리적으로 없는 값"임을 시각적으로 표시한다(실데이터 반영 중 빈 실데이터와 구분).
 */
export const BINDING_PLACEHOLDER_UNRESOLVABLE_COLOR = '#d97706';

/** 'unresolvable-only' 모드 placeholder의 title 툴팁 문구(표시 지점 공통). */
export const BINDING_PLACEHOLDER_UNRESOLVABLE_TITLE =
  '보고서 생성 후 쌓이는 값 — 모사로 지정해 그려 볼 수 있습니다';

/**
 * 이 바인딩의 값이 **저작(미리보기) 중엔 원리적으로 없는 값**인지 — 'unresolvable-only' 모드 판정 정본.
 *
 * - `lifecycle:'live'`: 보고서 생성 **후** 쌓이는 값(서명 등) — 저작 미리보기엔 박제 사건이 없다.
 * - catalog entry `reportScoped === true`: 컨텍스트(조직·현장·점검)에 원천이 없고 보고서 문서
 *   자체에 속하는 값(제목·차수·결재 서명 등, 2026-09-04 자동연결 폐기 결정) — snapshot이어도 부재.
 *
 * catalog 미주입/entry 미발견이면 lifecycle 축으로만 판정한다(과표시보다 미표시 — 빈칸이 안전).
 */
export function isAuthoringUnresolvable(
  binding: PlaceholderBinding,
  catalog?: CatalogIndex,
): boolean {
  if (binding.lifecycle === 'live') return true;
  const entry = binding.catalogKey ? catalog?.[binding.catalogKey] : undefined;
  return entry?.reportScoped === true;
}

// ── 값 출처(provenance) 표시 ────────────────────────────────────────────────
//
// 위 placeholder 축이 **빈값**의 이유("저작 중엔 원리적으로 없는 값")를 말한다면, 이 축은
// **차 있는 값**의 출처를 말한다. 게이트가 서로 배타(빈값 ↔ 실값)라 한 칸에 둘이 겹치지 않는다.
//
// 시각은 **점선 밑줄 + 색**뿐이다(인라인 style) — 배지·여백·아이콘을 붙이면 저작 캔버스와 실폼의
// 레이아웃이 갈라진다(WYSIWYG 원칙). 밑줄은 흐름 크기를 바꾸지 않아 페이지 분할 측정에도 중립이다.
// rc 패키지에서 새 Tailwind 유틸을 쓰지 않는 규약(앱 CSS 미생성 함정)도 인라인 style로 지킨다.

/** 모사 값 밑줄 색 — violet-600. amber(미해소)·gray(바인딩 보기)와 겹치지 않는 톤. */
export const BINDING_PROVENANCE_SIMULATED_COLOR = '#7c3aed';

/** 연결 보고서 저장값 밑줄 색 — blue-600. */
export const BINDING_PROVENANCE_LINKED_COLOR = '#2563eb';

/** 모사 값 배경 틴트 — violet-600 10%(반투명이라 기존 셀 배경 위에 겹침). */
export const BINDING_PROVENANCE_SIMULATED_TINT = 'rgba(124, 58, 237, 0.1)';

/** 연결 보고서 저장값 배경 틴트 — blue-600 10%. */
export const BINDING_PROVENANCE_LINKED_TINT = 'rgba(37, 99, 235, 0.1)';

/** 모사 값 title 툴팁 문구. */
export const BINDING_PROVENANCE_SIMULATED_TITLE = '모사 값';

/** 연결 보고서 저장값 title 툴팁 문구. */
export const BINDING_PROVENANCE_LINKED_TITLE = '연결한 보고서의 저장값';

/** 값 출처 갈래 — 모사 값(저작자 지정) / 연결한 보고서의 저장값. */
export type BindingProvenanceKind = 'simulated' | 'linked';

/** provenance 판정 ctx — RenderContext의 관련 축만(구조적 부분집합). */
interface ProvenanceCtx {
  bindingProvenance?: BindingProvenanceContext;
  catalog?: CatalogIndex;
}

/** 표시 지점 공통 — 갈래별 밑줄 색. */
export function bindingProvenanceColor(kind: BindingProvenanceKind): string {
  return kind === 'simulated'
    ? BINDING_PROVENANCE_SIMULATED_COLOR
    : BINDING_PROVENANCE_LINKED_COLOR;
}

/** 표시 지점 공통 — 갈래별 배경 틴트. */
export function bindingProvenanceTint(kind: BindingProvenanceKind): string {
  return kind === 'simulated'
    ? BINDING_PROVENANCE_SIMULATED_TINT
    : BINDING_PROVENANCE_LINKED_TINT;
}

/** 표시 지점 공통 — 갈래별 툴팁 문구. */
export function bindingProvenanceTitle(kind: BindingProvenanceKind): string {
  return kind === 'simulated'
    ? BINDING_PROVENANCE_SIMULATED_TITLE
    : BINDING_PROVENANCE_LINKED_TITLE;
}

/**
 * 이 카탈로그 키가 보고서 문서 자체에 속하는 값(`reportScoped`)인지.
 * 재료로 온 키 집합이 우선이고(팝아웃처럼 카탈로그 미주입 surface), 없으면 카탈로그로 폴백한다
 * — 둘은 같은 사실을 보므로 어느 쪽이든 `isAuthoringUnresolvable`의 reportScoped 축과 일치한다.
 */
function isReportScopedKey(catalogKey: string, ctx: ProvenanceCtx): boolean {
  const keys = ctx.bindingProvenance?.reportScopedKeys;
  if (keys) return keys.has(catalogKey);
  return ctx.catalog?.[catalogKey]?.reportScoped === true;
}

/**
 * 이 바인딩이 읽는 값의 **출처** 갈래 — 값 유무는 보지 않는다(호출부가 실값 갈래에서만 부른다).
 *
 * 우선순위(겹치지 않는다 — 아래 배타 주석):
 *  1. 모사 축 키(`${catalogKey}.${select}`)가 재료에 있으면 `'simulated'`
 *  2. 「보고서 연결」 중 + `reportScoped` entry면 `'linked'`
 *  3. 그 외 undefined(= 표시 없음. 컨텍스트 실데이터는 미리보기의 기본이라 표시가 노이즈다)
 *
 * ⚠️ **배타는 재료 쪽에서 이미 성립한다.** 연결 중이면 클라가 값 모사를 전송에서 벗기므로
 * (admin `toApiPreviewContext`) 재료의 `simulatedAxisKeys`도 비어 있다 — 1과 2가 동시에 참인
 * 상황이 애초에 만들어지지 않는다. 여기 순서는 재료가 어긋났을 때의 방어일 뿐이다.
 *
 * `select`가 없는 바인딩(entry 통째 참조)은 모사 축과 대응이 없어 1을 타지 않는다(과표시보다 미표시).
 */
export function bindingProvenanceKind(
  binding: PlaceholderBinding | undefined,
  ctx: ProvenanceCtx,
): BindingProvenanceKind | undefined {
  const provenance = ctx.bindingProvenance;
  if (!provenance) return undefined;
  const catalogKey = binding?.catalogKey;
  if (!catalogKey) return undefined;
  if (
    binding?.select !== undefined &&
    provenance.simulatedAxisKeys?.has(`${catalogKey}.${binding.select}`)
  ) {
    return 'simulated';
  }
  if (provenance.linkedReport === true && isReportScopedKey(catalogKey, ctx)) return 'linked';
  return undefined;
}

/** 값 출처 표식 — 표시 지점이 그대로 병합할 인라인 style + title. */
export interface BindingProvenanceMark {
  /** 레이아웃 중립 장식(점선 밑줄) — 노드/셀 style에 병합. */
  style: CSSProperties;
  /** title 툴팁 문구. */
  title: string;
}

/**
 * 실값이 렌더되는 갈래에서의 출처 표식 — 없으면 undefined(기존 렌더 그대로, 회귀 0).
 *
 * 게이트: 재료 주입 + catalogKey 보유 + **값이 빈값이 아님**. 빈값은 placeholder 축의 영역이라
 * (`bindingPlaceholderText`) 여기서 표식을 내면 빈칸에 밑줄만 남는다.
 */
export function bindingProvenanceMark(
  binding: PlaceholderBinding | undefined,
  value: unknown,
  ctx: ProvenanceCtx,
): BindingProvenanceMark | undefined {
  if (isEmptyRendered(value)) return undefined;
  const kind = bindingProvenanceKind(binding, ctx);
  if (kind === undefined) return undefined;
  return {
    style: {
      textDecoration: 'underline dotted',
      textDecorationColor: bindingProvenanceColor(kind),
      // 배경 틴트(2026-09-04 사용자 요청 — 밑줄만으론 저시인) — 반투명이라 셀 자체 배경(라벨
      // 회색 등) 위에서도 겹쳐 보이고, 레이아웃 중립(배경은 흐름에 무관)이라 WYSIWYG 훼손 없음.
      backgroundColor: bindingProvenanceTint(kind),
    },
    title: bindingProvenanceTitle(kind),
  };
}

/**
 * 표시 지점 공통 — 현재 모드의 placeholder 색. 'unresolvable-only'에서 표시되는 placeholder는
 * 전부 그 모드 판정을 통과한 것이므로(게이트가 걸러냄) ctx만으로 톤이 정해진다.
 */
export function bindingPlaceholderColor(ctx: {
  bindingPlaceholders?: BindingPlaceholdersMode;
}): string {
  return ctx.bindingPlaceholders === 'unresolvable-only'
    ? BINDING_PLACEHOLDER_UNRESOLVABLE_COLOR
    : BINDING_PLACEHOLDER_COLOR;
}

/** 표시 지점 공통 — 'unresolvable-only' 모드일 때만 안내 툴팁 문구(그 외 undefined = 기존 title 유지). */
export function bindingPlaceholderTitle(ctx: {
  bindingPlaceholders?: BindingPlaceholdersMode;
}): string | undefined {
  return ctx.bindingPlaceholders === 'unresolvable-only'
    ? BINDING_PLACEHOLDER_UNRESOLVABLE_TITLE
    : undefined;
}

/** render 표시상 "빈값"인지(null/undefined/'') 판정 — placeholder 표시 게이트. */
function isEmptyRendered(value: unknown): boolean {
  return value === null || value === undefined || value === '';
}

/**
 * select 경로(dotted, 예 'site.name')의 끝 필드 label을 returns 트리에서 찾는다.
 * 각 CatalogField.path는 단일 세그먼트('name')일 수도, 다중('site.address')일 수도 있어
 * 정확 매칭 + 접두 매칭(중첩 하강)을 모두 수용한다. 미발견 시 undefined.
 */
function findLeafLabel(fields: CatalogField[], selectPath: string): string | undefined {
  for (const f of fields) {
    if (f.path === selectPath) return f.label;
    if (f.fields && f.fields.length > 0 && selectPath.startsWith(`${f.path}.`)) {
      const found = findLeafLabel(f.fields, selectPath.slice(f.path.length + 1));
      if (found) return found;
    }
  }
  return undefined;
}

/**
 * 바인딩의 placeholder label 텍스트.
 * 우선순위: catalog entry.returns에서 select 끝 필드 label > entry.label > select > bindingKey.
 * (catalog 미주입/미발견이면 select/bindingKey로 폴백 — 항상 무언가는 표시.)
 */
export function bindingPlaceholderLabel(binding: PlaceholderBinding, catalog?: CatalogIndex): string {
  const entry = binding.catalogKey ? catalog?.[binding.catalogKey] : undefined;
  if (entry) {
    if (binding.select) {
      const leaf = findLeafLabel(entry.returns, binding.select);
      if (leaf) return leaf;
    }
    if (entry.label) return entry.label;
  }
  return binding.select ?? binding.bindingKey;
}

/**
 * placeholder 텍스트('{label}')에 template format을 랩핑한다.
 *
 * format이 '{value}'를 참조하는 template kind일 때만 applyFormat으로 랩핑해 단위/접미어를
 * 라벨과 함께 표시한다(예: '{근로자 수}' + '{value}명' → '{근로자 수}명'). 그 외(format 없음 /
 * template 아님 / '{value}' 미참조)는 원 텍스트 그대로 반환(회귀 0).
 */
export function wrapPlaceholderWithFormat(
  text: string,
  format: BindingFormat | undefined,
  scope: unknown,
): string {
  if (format?.kind === 'template' && /\{value\}/.test(format.template)) {
    return String(applyFormat(text, format, scope));
  }
  return text;
}

/**
 * 노드/셀 binding에 대한 placeholder 텍스트('{label}')를 산출한다.
 *
 * ctx.bindingPlaceholders가 켜져 있고 binding.catalogKey가 있으며 resolve 값이 빈값일 때만 반환.
 * 그 외(옵션 off / catalogKey 없음 / 실값 있음)는 undefined → 기존 동작 그대로(회귀 0).
 * 'unresolvable-only' 모드는 추가로 isAuthoringUnresolvable(live/보고서-스코프) 판정을 통과해야
 * 반환한다(그 외 빈값은 실렌더처럼 빈칸 — 진짜 빈 실데이터와 원리적 부재의 구분이 목적).
 *
 * format(template kind, '{value}' 참조)이 주어지면 라벨에 단위/접미어를 함께 랩핑해 표시한다
 * (예: '{근로자 수}명'). scope는 template의 '{a.b}' 보조 경로 조회용.
 */
export function bindingPlaceholderText(
  binding: PlaceholderBinding | undefined,
  value: unknown,
  ctx: PlaceholderCtx,
  format?: BindingFormat,
  scope?: unknown,
): string | undefined {
  if (!ctx.bindingPlaceholders) return undefined;
  if (!binding?.catalogKey) return undefined;
  if (!isEmptyRendered(value)) return undefined;
  if (
    ctx.bindingPlaceholders === 'unresolvable-only' &&
    !isAuthoringUnresolvable(binding, ctx.catalog)
  ) {
    return undefined;
  }
  return wrapPlaceholderWithFormat(`{${bindingPlaceholderLabel(binding, ctx.catalog)}}`, format, scope);
}

/**
 * 편집 캔버스 전용 — repeat 패턴 셀(데이터 0이라 빈값)에 어느 필드에 바인딩됐는지
 * `{필드라벨}`을 표시한다(편집 보조). 카탈로그 placeholder(catalogKey 경로)와 별개로,
 * dataDef/카탈로그 배열 필드 등 catalogKey 없는 repeat 바인딩을 ctx.bindingLabels로 라벨링한다.
 *
 * ctx.editing이고 값이 빈값이며 bindingLabels에 그 bindingKey 라벨이 있을 때만 반환.
 * 그 외(편집 아님 / 실값 있음 / 라벨 없음)는 undefined → 기존 동작 그대로(회귀 0).
 *
 * format(template kind, '{value}' 참조)이 주어지면 라벨에 단위/접미어를 함께 랩핑해 표시한다.
 */
export function bindingLabelPlaceholderText(
  binding: { bindingKey: string } | undefined,
  value: unknown,
  ctx: { editing?: boolean; bindingLabels?: Record<string, string> },
  format?: BindingFormat,
  scope?: unknown,
): string | undefined {
  if (!ctx.editing) return undefined;
  if (!binding) return undefined;
  if (!isEmptyRendered(value)) return undefined;
  const label = ctx.bindingLabels?.[binding.bindingKey];
  if (!label) return undefined;
  return wrapPlaceholderWithFormat(`{${label}}`, format, scope);
}

/**
 * 반복(repeat) 0행 빈 힌트 텍스트 — 'unresolvable-only' 모드(실데이터 반영 중) 전용.
 *
 * 실데이터 0행이면 반복 단위가 통째로 사라져 캔버스/미리보기에 아무것도 안 남는다(자동연결 폐기로
 * tbm-sessions가 gap이 된 실측 사례 — 저작자가 원인을 알 수 없음). 소스 라벨과 0행 사실을 한 줄로
 * 알리고, 그 소스가 **행 수 모사 대상**(assembled + list + editMode:'readonly' — admin
 * derivePreviewSimulationAxes의 행 수 축 파생과 같은 조건)이면 모사 안내 꼬리를 잇는다.
 * editable/manual 소스는 꼬리 생략 — 그 행은 작성폼에서 늘리는 것이라 모사 안내가 거짓이 된다.
 *
 * over가 선언된 dataSource와 매칭되지 않으면(행 스코프 필드 반복·레거시 경로) undefined —
 * 라벨을 모르는 채 힌트를 지어내지 않는다(중첩 반복 노이즈 방지, 기존 빈 컨테이너 유지).
 */
export function repeatEmptyHintText(
  over: string,
  dataSources?: Pick<DataSourceDef, 'key' | 'label' | 'kind' | 'cardinality' | 'editMode'>[],
): string | undefined {
  const def = dataSources?.find((d) => d.key === over);
  if (!def) return undefined;
  const simulatable =
    def.kind === 'assembled' && def.cardinality === 'list' && def.editMode === 'readonly';
  return `{${def.label}} 실데이터 0행${simulatable ? ' — 행 수 모사로 늘려 볼 수 있습니다' : ''}`;
}

/** 계산 셀 op → 라벨(한글). mode='expr'은 '계산'. placeholder + admin 요약(cellDefSummary) 공유 소스. */
export const COMPUTE_OP_PLACEHOLDER_LABEL: Record<CellComputeOp, string> = {
  sum: '합계',
  subtract: '차',
  multiply: '곱',
  divide: '나눗셈',
  avg: '평균',
  min: '최소',
  max: '최대',
};

/** 계산 셀의 placeholder 라벨 — mode='expr'은 '계산', op 모드는 op별 한글(미지정 op는 '계산'). */
export function computePlaceholderLabel(compute: Pick<CellCompute, 'mode' | 'op'>): string {
  if (compute.mode === 'expr') return '계산';
  return (compute.op && COMPUTE_OP_PLACEHOLDER_LABEL[compute.op]) ?? '계산';
}

/**
 * 계산 셀 placeholder 텍스트 — 바인딩 보기(ctx.bindingPlaceholders)에서 compute의 바인딩 입력이
 * **모두 미해석(빈값)**이면 가짜 0 대신 `{합계}`류 placeholder를 반환한다.
 *
 * 게이트(모두 만족해야 반환):
 *   - 옵션 on
 *   - binding 입력(const 아님 + binding 존재)이 1개 이상 — **상수만 있는 compute는 제외**(데이터 무관, 계산 표시)
 *   - 그 binding 입력 전부가 scope에서 미해석(readBinding 결과가 빈값). **일부만 해석되면 제외**(기존 0-간주 계산 유지)
 *   - 'unresolvable-only' 모드는 추가로 binding 입력 **전부**가 isAuthoringUnresolvable이어야 반환
 *     (하나라도 일반 snapshot이면 실데이터로 채워질 수 있는 계산 — 기존 0-간주 계산 유지)
 *
 * format(template kind, '{value}' 참조)이면 라벨에 단위/접미어를 함께 랩핑한다(예 `{합계}명`).
 * readBinding은 RenderTemplate의 readBindingValue(scope, key) 경로(flat 우선 + getByPath fallback).
 */
export function computePlaceholderText(
  compute: CellCompute,
  ctx: PlaceholderCtx,
  readBinding: (bindingKey: string) => unknown,
  scope?: unknown,
): string | undefined {
  if (!ctx.bindingPlaceholders) return undefined;
  // const 우선(computeInputValue와 동일 판정): const 지정 입력은 상수, 그 외 binding 보유는 바인딩 입력.
  const bindingInputs = compute.inputs.filter((i) => i.const === undefined && i.binding);
  if (bindingInputs.length === 0) return undefined; // 상수만 → 계산 표시
  const allUnresolved = bindingInputs.every((i) =>
    isEmptyRendered(readBinding(i.binding!.bindingKey)),
  );
  if (!allUnresolved) return undefined; // 일부라도 해석 → 기존 동작(0 간주 계산)
  if (
    ctx.bindingPlaceholders === 'unresolvable-only' &&
    !bindingInputs.every((i) => isAuthoringUnresolvable(i.binding!, ctx.catalog))
  ) {
    return undefined; // 일반 빈 실데이터가 섞인 계산 → 실렌더처럼 0-간주 계산
  }
  return wrapPlaceholderWithFormat(`{${computePlaceholderLabel(compute)}}`, compute.format, scope);
}

/** 집계 함수 → 라벨(한글). placeholder + admin 요약(cellDefSummary) 공유 소스. */
export const AGGREGATE_FN_PLACEHOLDER_LABEL: Record<CellAggregateFn, string> = {
  count: '개수',
  sum: '합계',
  avg: '평균',
  min: '최소',
  max: '최대',
};

/** 집계 셀의 placeholder 라벨 — fn별 한글. */
export function aggregatePlaceholderLabel(agg: Pick<CellAggregate, 'fn'>): string {
  return AGGREGATE_FN_PLACEHOLDER_LABEL[agg.fn] ?? '집계';
}

/**
 * 집계 셀 placeholder 텍스트 — 바인딩 보기(ctx.bindingPlaceholders)에서 집계 대상 배열(over)이
 * **미적용(undefined/null)**이면 가짜 0 대신 `{개수}`/`{합계}`류 placeholder를 반환한다.
 *
 * over(=getByPath(scope, agg.over))가 undefined/null일 때만 반환. **빈 배열([])은 실데이터 0건**이므로
 * 제외(기존 0 표시 유지). format(template '{value}' 참조)이면 라벨에 단위/접미어를 랩핑한다.
 *
 * 'unresolvable-only' 모드는 항상 미반환(=off와 동일) — over는 데이터 경로뿐이라 lifecycle/
 * reportScoped를 판정할 binding 정보가 없다. 판정 불가면 과표시 대신 실렌더 동작(빈칸/0)을 따른다.
 */
export function aggregatePlaceholderText(
  agg: CellAggregate,
  ctx: PlaceholderCtx,
  over: unknown,
  scope?: unknown,
): string | undefined {
  if (!ctx.bindingPlaceholders) return undefined;
  if (ctx.bindingPlaceholders === 'unresolvable-only') return undefined; // 판정 불가 → 실렌더 동작
  if (over !== undefined && over !== null) return undefined; // 빈 배열 포함 실데이터면 계산
  return wrapPlaceholderWithFormat(`{${aggregatePlaceholderLabel(agg)}}`, agg.format, scope);
}
